import React, { useState, useEffect } from 'react';
import {
  Car, TrendingUp, Shield, AlertTriangle,
  ChevronRight, CheckCircle, DollarSign, Award,
  Users, Building, Zap, Clock, ArrowRight, Star,
  LineChart, Briefcase, Target, TrendingDown, Lock,
  Eye, Repeat
} from 'lucide-react';

const CarSalesDemo = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [retentionScore, setRetentionScore] = useState(300);
  const [groupExposure, setGroupExposure] = useState(0);
  const videoRef = React.useRef<HTMLDivElement>(null);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === 'M@ke@llth31mpactComcast!!' || passwordInput === '1wantahouseintheHamptonswithColby!') {
      setIsAuthenticated(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const scrollToVideo = () => {
    videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    setIsVisible(true);
    // Animate Retention Health Score
    const scoreInterval = setInterval(() => {
      setRetentionScore(prev => {
        if (prev >= 720) {
          clearInterval(scoreInterval);
          return 720;
        }
        return prev + 5;
      });
    }, 20);

    // Animate annual group turnover exposure
    const valueInterval = setInterval(() => {
      setGroupExposure(prev => {
        if (prev >= 1700000) {
          clearInterval(valueInterval);
          return 1700000;
        }
        return prev + 22000;
      });
    }, 20);

    return () => {
      clearInterval(scoreInterval);
      clearInterval(valueInterval);
    };
  }, []);

  const features = [
    {
      icon: <LineChart className="w-6 h-6" />,
      title: "Model Mix → Commission Fix",
      description: "Adjust the floor's model mix and watch commission strategy recalculate—then publish it live to every agent through CarSalesCoach in real time",
      benefit: "A management decision becomes floor behavior—no meeting required"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "OEM Volume Bonus Tracker",
      description: "See exactly how far each rooftop sits from its OEM threshold, which models carry manufacturer spiffs, and what's at stake—$1,850 per agent—with the clock running",
      benefit: "Capture the bonus instead of losing it to a missed deadline"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Agent Retention Cost Model",
      description: "Model your group's true turnover exposure, then drag the exit-floor slider and watch flight-risk agents turn green as transparency lifts take-home pay",
      benefit: "See the seven-figure problem written down—and the fix that pays for itself"
    }
  ];

  const stats = [
    { number: "80%", label: "salesperson turnover at the average dealer", source: "Cox Automotive Staffing Study" },
    { number: "$12,000", label: "conservative cost of a single agent departure", source: "Industry Estimate" },
    { number: "18 mo", label: "average tenure vs. 3 years to peak productivity", source: "NADA Workforce Study" },
    { number: "46%", label: "actual turnover vs. the 22% executives report", source: "Cox Automotive" }
  ];

  const democratizationPoints = [
    {
      title: "CarSalesCoach — Open the Black Box",
      icon: <Eye className="w-8 h-8" />,
      before: "Commission is a black box—pack fees, tiered rates, chargebacks, F&I splits, draws. Agents can't read their own pay, lose trust, and leave.",
      after: "Every agent sees their exact gross on every deal, tier progress, and next-month projection—in plain English.",
      features: [
        "Real-time earnings on every deal",
        "Volume-tier progress tracking",
        "F&I impact and chargeback visibility",
        "What the month looks like after the next three deals"
      ]
    },
    {
      title: "RooftopOptimize — Control the Floor",
      icon: <Building className="w-8 h-8" />,
      before: "Changing floor strategy means a meeting, a printout, or an announcement half the team never sees—and management drives to every store for the data.",
      after: "Push F&I priorities, OEM incentives, and lot strategy to every agent's screen the moment you decide.",
      features: [
        "Dynamic strategy channel to every seat",
        "OEM volume bonus tracker",
        "Live per-rooftop flight-risk view",
        "Monthly turnover cost and retention ROI reports"
      ]
    },
    {
      title: "CarAssetCoach — Close the Flywheel",
      icon: <Repeat className="w-8 h-8" />,
      before: "Conquest leads cost $340–$1,735 each, and your last buyer drifts to a third-party marketplace by the time they're ready to trade.",
      after: "A 60-second gift at closing brings that buyer back to your floor when they're ready to replace—for $49.",
      features: [
        "60-second gift handed off at closing",
        "Daily-use consumer vehicle intelligence app",
        "Replace-signal routed back to your floor",
        "$49 returning-lead cost vs. $340–$1,735 conquest"
      ]
    }
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Required</h1>
            <p className="text-gray-600">Please enter the password to access this page</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  setPasswordError(false);
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  passwordError
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Enter password"
                autoFocus
              />
              {passwordError && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Incorrect password. Please try again.
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Access Page
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-20 sm:px-6 lg:px-8">
          <div className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            {/* Hero Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-blue-800/50 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-600">
                <Car className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium">The Operating System for Dealer Groups</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                Run Your Dealership Like
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 mt-2">
                  An Asset Manager.
                </span>
              </h1>

              <p className="text-xl text-blue-100 leading-relaxed">
                Dealer groups spend heavily on technology and marketing—and almost nothing on keeping the
                people who sell the cars. <span className="font-semibold text-white">Salesperson turnover runs 80%,
                costing roughly $12,000 every time a seat empties.</span> OhmCar makes commission transparent,
                floor strategy instant, and every sale the seed of the next one.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button className="group bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center justify-center">
                  See the Live Demo
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={scrollToVideo} className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-sm">
                  See How It Works
                </button>
              </div>

              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Built with floor staff</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>DMA exclusivity included</span>
                </div>
              </div>
            </div>

            {/* Hero Visual - RooftopOptimize Console */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 shadow-2xl">
                <div className="space-y-6">
                  {/* Group Exposure Display */}
                  <div className="text-center border-b border-white/20 pb-6">
                    <div className="text-sm font-medium text-blue-200 mb-2">ANNUAL GROUP TURNOVER EXPOSURE</div>
                    <div className="text-4xl font-bold mb-2">${(groupExposure).toLocaleString()}</div>
                    <div className="flex items-center justify-center space-x-2 text-green-400 text-sm">
                      <TrendingDown className="w-4 h-4" />
                      <span>$9,600/rooftop/yr recoverable</span>
                    </div>
                  </div>

                  {/* Retention Health Score Display */}
                  <div className="text-center">
                    <div className="text-sm font-medium text-blue-200 mb-4">RETENTION HEALTH SCORE</div>
                    <div className="relative inline-block">
                      <svg className="transform -rotate-90 w-40 h-40">
                        <circle
                          cx="80"
                          cy="80"
                          r="72"
                          stroke="rgba(255,255,255,0.1)"
                          strokeWidth="10"
                          fill="none"
                        />
                        <circle
                          cx="80"
                          cy="80"
                          r="72"
                          stroke="url(#gradient)"
                          strokeWidth="10"
                          fill="none"
                          strokeDasharray={`${(retentionScore - 300) / 550 * 452} 452`}
                          className="transition-all duration-300"
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#10B981" />
                            <stop offset="100%" stopColor="#3B82F6" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-4xl font-bold">{retentionScore}</div>
                          <div className="text-xs text-blue-200">Strong</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                      <div className="text-lg font-bold text-yellow-400">12</div>
                      <div className="text-xs text-blue-200">Flight-Risk Agents</div>
                    </div>
                    <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                      <div className="text-lg font-bold text-green-400">$1,850</div>
                      <div className="text-xs text-blue-200">OEM Bonus / Agent</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section ref={videoRef} className="bg-gray-50 py-20">
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              See the Platform in Action
            </h2>
            <p className="text-xl text-gray-600">
              The three-module walkthrough: model mix, OEM bonus tracker, and the retention cost model
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black w-3/4 mx-auto">
            <video
              controls
              className="w-full"
              poster=""
            >
              <source src="/YC_AiCarAssetCoachAiCarSalesCoach_2.MOV" type="video/quicktime" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="bg-white border-y border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-slate-900 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600 mb-1">{stat.label}</div>
                <div className="text-xs text-gray-400">{stat.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Products Story */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Three Products. One Flywheel.
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Agents stay when they can see their pay. Management moves when strategy reaches every screen.
              And every sale you keep seeds the next one—at a fraction of conquest cost. Cars, careers, and
              dealerships, managed as assets.
            </p>
          </div>

          <div className="space-y-12">
            {democratizationPoints.map((point, idx) => (
              <div key={idx} className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="grid lg:grid-cols-2 gap-8 p-8">
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-slate-100 rounded-xl text-slate-700">
                        {point.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{point.title}</h3>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                        <div className="text-sm font-semibold text-red-800 mb-1">Before</div>
                        <div className="text-sm text-red-700">{point.before}</div>
                      </div>

                      <div className="flex justify-center">
                        <ArrowRight className="w-6 h-6 text-gray-400" />
                      </div>

                      <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                        <div className="text-sm font-semibold text-green-800 mb-1">Now</div>
                        <div className="text-sm text-green-700">{point.after}</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4">What's Inside:</h4>
                    <ul className="space-y-3">
                      {point.features.map((feature, featureIdx) => (
                        <li key={featureIdx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features — The Demo Modules */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              The Demo: Three Modules
            </h2>
            <p className="text-xl text-gray-600">
              Everything a dealer group needs to see the cost of turnover—and the system that turns it
              into retention, control, and returning leads
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className={`group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border-2 cursor-pointer transition-all duration-300 ${
                  activeFeature === idx
                    ? 'border-blue-600 shadow-2xl scale-105'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-xl'
                }`}
                onMouseEnter={() => setActiveFeature(idx)}
              >
                <div className={`inline-flex p-4 rounded-xl mb-6 transition-all duration-300 ${
                  activeFeature === idx
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 group-hover:bg-blue-100'
                }`}>
                  {feature.icon}
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>

                <p className="text-gray-600 mb-4 leading-relaxed">
                  {feature.description}
                </p>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-start">
                    <Award className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-blue-800 font-medium">{feature.benefit}</span>
                  </div>
                </div>

                <div className={`mt-6 flex items-center text-blue-600 font-semibold transition-all duration-300 ${
                  activeFeature === idx ? 'translate-x-2' : ''
                }`}>
                  Learn More
                  <ChevronRight className="w-5 h-5 ml-1" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              From Hidden Cost to Returning Lead
            </h2>
            <p className="text-xl text-gray-600">
              Four moves that close the loop
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="font-bold text-lg mb-2">Model Your Exposure</h3>
              <p className="text-gray-600 text-sm">See the real turnover cost across every rooftop</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="font-bold text-lg mb-2">Open the Black Box</h3>
              <p className="text-gray-600 text-sm">Give every agent transparent, real-time pay</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="font-bold text-lg mb-2">Push Strategy Live</h3>
              <p className="text-gray-600 text-sm">Send F&I and OEM priorities to every screen instantly</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
              <h3 className="font-bold text-lg mb-2">Close the Flywheel</h3>
              <p className="text-gray-600 text-sm">Gift CarAssetCoach at closing and bring buyers back</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Become the Founding Partner in Your DMA
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            The first three groups per market lock in founding-partner pricing—and lock competitors out
            of the flywheel in their territory. $700 per rooftop per month, backed by a 12-month outcome
            guarantee: miss the targets and 50% comes back.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-900 px-10 py-5 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center justify-center group">
              Request Your Founding-Partner Demo
              <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-2 transition-transform" />
            </button>
            <button onClick={scrollToVideo} className="border-2 border-white text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-sm">
              See Demo Video
            </button>
          </div>

          <div className="mt-8 flex items-center justify-center space-x-8 text-sm">
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-yellow-400" />
              <span>DMA exclusivity</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-green-400" />
              <span>12-month outcome guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-bold mb-4">Platform</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">CarSalesCoach</a></li>
                <li><a href="#" className="hover:text-white transition-colors">RooftopOptimize</a></li>
                <li><a href="#" className="hover:text-white transition-colors">CarAssetCoach</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Free Turnover Cost Report</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Retention ROI Model</a></li>
                <li><a href="#" className="hover:text-white transition-colors">DMA Exclusivity Map</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Partners</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Dealer Groups</a></li>
                <li><a href="#" className="hover:text-white transition-colors">OEM Programs</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Enterprise</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm">© 2026 OhmCar. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CarSalesDemo;
